import React, { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, Table, Thead, Tbody, Tr, Th, Td, Checkbox, Text, useToast, Spinner, Center,
    InputGroup, InputLeftElement, Input, Box, List, ListItem, Badge, IconButton, Tooltip,
    InputRightElement, Flex, TableContainer
} from '@chakra-ui/react';
import { FiSearch, FiTrash2, FiUserPlus, FiX } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getGradeLabelOverride } from '../utils/gradeLabels';

const ManageClassStudentsModal = ({ isOpen, onClose, classItem, onStudentCountChange }) => {
    // [A2] 年級顯示依登入者機構 grade_labels 渲染；沒設定（天籟現況）時 fallback 回現行文字
    const { user } = useAuth();
    const gradeLabels = user?.institution?.grade_labels || null;
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    // Search states
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Processing states
    const [processingIds, setProcessingIds] = useState(new Set());
    const [batchProcessing, setBatchProcessing] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (isOpen && classItem) {
            fetchClassStudents();
            setSearchTerm('');
            setSearchResults([]);
            setSelectedIds([]);
        }
    }, [isOpen, classItem]);

    const fetchClassStudents = async () => {
        setLoading(true);
        try {
            // Fetch students belonging to this class
            const res = await authAPI.getStudents({ class_id: classItem.id, limit: 1000 });
            setStudents(res.data);
            if (onStudentCountChange) {
                onStudentCountChange(classItem.id, res.data.length);
            }
        } catch (error) {
            toast({ title: '無法獲取班級學生', status: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm.trim().length >= 1) {
                performSearch(searchTerm.trim());
            } else {
                setSearchResults([]);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const performSearch = async (term) => {
        setSearching(true);
        try {
            const res = await authAPI.getStudents({ search: term, limit: 10 });
            // Filter out students already in this class
            const existingIds = new Set(students.map(s => s.id));
            const filtered = res.data.filter(s => !existingIds.has(s.id));
            setSearchResults(filtered);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setSearching(false);
        }
    };

    const getStudentClassIds = (student) => {
        const v2ids = student.classes ? student.classes.map(c => c.id) : [];
        if (v2ids.length === 0 && student.class_id) {
            v2ids.push(student.class_id);
        }
        return [...new Set(v2ids)]; // Unique
    };

    const handleAddStudent = async (student) => {
        setProcessingIds(prev => new Set(prev).add(student.id));
        try {
            const currentIds = getStudentClassIds(student);
            if (currentIds.includes(classItem.id)) return; // Already in class

            const updatedClassIds = [...currentIds, classItem.id];

            await authAPI.updateStudent(student.id, { class_ids: updatedClassIds });
            toast({ title: '已加入班級', status: 'success' });

            // Clean up search and refresh
            setSearchTerm('');
            setSearchResults([]);
            fetchClassStudents();
        } catch (error) {
            toast({ title: '加入失敗', status: 'error' });
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(student.id);
                return next;
            });
        }
    };

    const handleRemoveStudent = async (studentId) => {
        if (!window.confirm("確定要將這位學生從班級中移除嗎？")) return;

        setProcessingIds(prev => new Set(prev).add(studentId));
        try {
            const student = students.find(s => s.id === studentId);
            const currentIds = getStudentClassIds(student);
            const updatedClassIds = currentIds.filter(id => id !== classItem.id);

            await authAPI.updateStudent(studentId, { class_ids: updatedClassIds });
            toast({ title: '已移除', status: 'success' });

            fetchClassStudents();
            setSelectedIds(prev => prev.filter(id => id !== studentId));
        } catch (error) {
            toast({ title: '移除失敗', status: 'error' });
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(studentId);
                return next;
            });
        }
    };

    const handleBatchRemove = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`確定要將選取的 ${selectedIds.length} 位學生從班級中移除嗎？`)) return;

        setBatchProcessing(true);
        try {
            const promises = selectedIds.map(studentId => {
                const student = students.find(s => s.id === studentId);
                const currentIds = getStudentClassIds(student);
                const updatedClassIds = currentIds.filter(id => id !== classItem.id);
                return authAPI.updateStudent(studentId, { class_ids: updatedClassIds });
            });

            await Promise.all(promises);
            toast({ title: '批量移除成功', status: 'success' });

            fetchClassStudents();
            setSelectedIds([]);
        } catch (error) {
            toast({ title: '部分移除發生錯誤', status: 'error' });
            fetchClassStudents();
        } finally {
            setBatchProcessing(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === students.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(students.map(s => s.id));
        }
    };

    const toggleSelect = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
        } else {
            setSelectedIds(prev => [...prev, id]);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent display="flex" flexDirection="column" maxH="90vh">
                <ModalHeader pb={2}>
                    管理班級學生
                    {classItem && <Text fontSize="md" color="gray.500" fontWeight="normal">{classItem.name}</Text>}
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody display="flex" flexDirection="column" gap={4}>
                    <Box zIndex={2} position="relative" bg="gray.50" p={4} borderRadius="md" borderWidth="1px">
                        <Text fontWeight="bold" mb={2} fontSize="sm">新增學生</Text>
                        <InputGroup size="md">
                            <InputLeftElement pointerEvents="none">
                                <FiSearch color="gray.300" />
                            </InputLeftElement>
                            <Input
                                placeholder="搜尋全校學生 (輸入姓名...)"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                bg="white"
                            />
                            {searchTerm && (
                                <InputRightElement>
                                    <IconButton
                                        icon={<FiX />}
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => setSearchTerm('')}
                                        aria-label="清除搜尋"
                                    />
                                </InputRightElement>
                            )}
                        </InputGroup>

                        {searchTerm && (
                            <Box
                                position="absolute"
                                top="100%"
                                left="0"
                                right="0"
                                mt={1}
                                bg="white"
                                shadow="lg"
                                borderRadius="md"
                                borderWidth="1px"
                                maxH="200px"
                                overflowY="auto"
                            >
                                {searching ? (
                                    <Center p={4}><Spinner size="sm" /></Center>
                                ) : searchResults.length > 0 ? (
                                    <List>
                                        {searchResults.map(student => (
                                            <ListItem
                                                key={student.id}
                                                p={3}
                                                _hover={{ bg: 'brand.50' }}
                                                borderBottomWidth="1px"
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Box>
                                                    <Text fontWeight="bold">{student.full_name}</Text>
                                                    {student.grade && <Text fontSize="xs" color="gray.500">{getGradeLabelOverride(student.grade, gradeLabels) || `${student.grade}年級`}</Text>}
                                                </Box>
                                                <Button
                                                    size="sm"
                                                    leftIcon={<FiUserPlus />}
                                                    colorScheme="brand"
                                                    variant="outline"
                                                    onClick={() => handleAddStudent(student)}
                                                    isLoading={processingIds.has(student.id)}
                                                >
                                                    加入
                                                </Button>
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Text p={4} color="gray.500" textAlign="center" fontSize="sm">找不到符合的學生，或該學生已在班級中</Text>
                                )}
                            </Box>
                        )}
                    </Box>

                    <Flex justify="space-between" align="center" mt={2}>
                        <Text fontWeight="bold">目前名單 ({students.length}人)</Text>
                        <Button
                            colorScheme="red"
                            size="sm"
                            variant="outline"
                            leftIcon={<FiTrash2 />}
                            isDisabled={selectedIds.length === 0}
                            onClick={handleBatchRemove}
                            isLoading={batchProcessing}
                        >
                            批量移除 ({selectedIds.length})
                        </Button>
                    </Flex>

                    <TableContainer flex="1" overflowY="auto" borderWidth="1px" borderRadius="md" minH="200px">
                        <Table size="sm" variant="simple" stickyHeader>
                            <Thead>
                                <Tr>
                                    <Th w="40px" bg="gray.100">
                                        <Checkbox
                                            isChecked={students.length > 0 && selectedIds.length === students.length}
                                            isIndeterminate={selectedIds.length > 0 && selectedIds.length < students.length}
                                            onChange={toggleSelectAll}
                                            isDisabled={students.length === 0}
                                        />
                                    </Th>
                                    <Th bg="gray.100">姓名</Th>
                                    <Th bg="gray.100">年級</Th>
                                    <Th w="80px" bg="gray.100">操作</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {loading && students.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={4}>
                                            <Center p={6}><Spinner color="brand.500" /></Center>
                                        </Td>
                                    </Tr>
                                ) : students.length > 0 ? (
                                    students.map(student => (
                                        <Tr key={student.id} bg={selectedIds.includes(student.id) ? 'red.50' : 'transparent'}>
                                            <Td>
                                                <Checkbox
                                                    isChecked={selectedIds.includes(student.id)}
                                                    onChange={() => toggleSelect(student.id)}
                                                />
                                            </Td>
                                            <Td fontWeight="bold">{student.full_name}</Td>
                                            <Td>
                                                {student.grade ? <Badge colorScheme="purple" variant="subtle">{getGradeLabelOverride(student.grade, gradeLabels) || `${student.grade}年級`}</Badge> : '-'}
                                            </Td>
                                            <Td>
                                                <Tooltip label="從班級移除" hasArrow>
                                                    <IconButton
                                                        icon={<FiTrash2 />}
                                                        size="xs"
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        onClick={() => handleRemoveStudent(student.id)}
                                                        isLoading={processingIds.has(student.id)}
                                                        aria-label="移除學生"
                                                    />
                                                </Tooltip>
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <Tr>
                                        <Td colSpan={4} textAlign="center" py={8} color="gray.500">
                                            班級內尚未加入任何學生
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </ModalBody>

                <ModalFooter>
                    <Button colorScheme="brand" onClick={onClose}>完成</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ManageClassStudentsModal;
